import React, { ReactNode, useRef, useState } from 'react'
import { Chip } from '@/components'
import { Button, Dialog, Fieldset, Form, Input, Label, Text, View, VisuallyHidden, XStack } from '@red-ui/components'
import { getString } from '@vrw/data'
import { formatPoints } from '@vrw/data/src/utils/formatters'
import { Link } from '@/components/Link'
import { regionalizePath } from '@/dataImplementation/viewingRegionWeb'
import { PATHS } from '@/router/paths'

const Option = ({ onPress, isActive, label }: { onPress: () => void; isActive: boolean; label: string }) => {
  const inputId = `option-${label}`

  return (
    <Label
      htmlFor={inputId}
      onPress={onPress}
      cursor="pointer"
      $gtTabletLandscape={{
        flexBasis: 0,
        flexGrow: 1,
        maxWidth: '$14',
      }}>
      <VisuallyHidden>
        <input type="radio" id={inputId} name="point-options" value={label} checked={isActive} style={{ pointerEvents: 'auto' }} readOnly />
      </VisuallyHidden>
      <Chip.Root
        flexGrow={1}
        minWidth="unset"
        borderWidth={1}
        height="$5"
        $gtTabletLandscape={{ paddingHorizontal: '$0' }}
        borderColor="$red6"
        shadowColor="$transparent"
        borderRadius="$9"
        backgroundColor={isActive ? '$red6' : '$white'}
        hoverStyle={{ backgroundColor: isActive ? '$red5' : '$red1' }}
        pressStyle={{ backgroundColor: isActive ? '$red7' : '$red2', borderColor: '$transparent' }}>
        <Chip.Text fontSize="$4" whiteSpace="nowrap" lineHeight="$6" color={isActive ? '$white' : '$black10'} fontWeight="400">
          {label}
        </Chip.Text>
      </Chip.Root>
    </Label>
  )
}

export const PointsPicker = ({
  balance,
  title,
  min = 1000,
  backLink = PATHS.MY_ACCOUNT_LINK_ACCOUNTS,
  renderDialogContent,
}: {
  balance: number
  title: string
  min?: number
  backLink?: string
  renderDialogContent: (value?: number) => ReactNode
}) => {
  const formattedBalance = formatPoints({ points: balance })
  const pointOptions = getAvailableOptions(balance)
  const [value, setValue] = useState(pointOptions[pointOptions.length - 1])
  const [isOtherSelected, setIsOtherSelected] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const isValid = value >= min && value <= balance
  const inputRef = useRef<Input>(null)

  const onSelectOption = (option: number) => () => {
    setIsOtherSelected(false)
    setValue(option)
  }

  const onSelectCustom = () => {
    setIsOtherSelected(true)
    inputRef.current?.focus()
  }

  const onChangeText = (value: string) => {
    if (!isOtherSelected) setIsOtherSelected(true)
    setValue(Number(value.replace(/[^\d]/g, '')))
  }

  const handleRequestTransfer = () => {
    setModalOpen(true)
  }

  const handleSubmit = () => {
    setModalOpen(false)
  }

  const handleCancel = () => {
    setModalOpen(false)
  }

  return (
    <>
      <Form marginTop="$7" $mobile={{ marginTop: '$3' }}>
        <View padding="$3" paddingBottom="$5" borderColor="$black2" borderWidth={1} borderRadius="$5">
          <Fieldset borderWidth="$0" padding="$0" margin="$0">
            <legend>
              <VisuallyHidden>{title}</VisuallyHidden>
            </legend>
            <Text fontSize="$5" fontWeight="600" marginBottom="$3">
              {title}
            </Text>
            <View role="radiogroup" gap="$1" marginBottom="$5" alignItems="baseline" $gtTabletLandscape={{ flexDirection: 'row' }}>
              {pointOptions.map((option) => (
                <Option
                  key={option}
                  onPress={onSelectOption(option)}
                  isActive={value === option && !isOtherSelected}
                  label={`${option} ${getString('misc.points.short')}`}
                />
              ))}
              <Option onPress={onSelectCustom} isActive={isOtherSelected} label={getString('pointsPicker.options.other')} />
            </View>
            <Input
              id="custom-points"
              ref={inputRef}
              size="$5"
              paddingLeft="$1"
              borderColor={isValid ? '$black2' : '$red6'}
              value={`${value}`}
              onChangeText={onChangeText}
              keyboardType="number-pad"
              maxLength={16}
              aria-invalid={isValid ? 'false' : 'true'}
            />
            <Label htmlFor="custom-points" color={isValid ? 'unset' : '$red6'} fontSize="$2" marginTop="$1">
              {isValid
                ? getString('pointsPicker.input.description')
                : getString('pointsPicker.input.error')
                    .replace('{{min}}', formatPoints({ points: min }))
                    .replace('{{balance}}', formattedBalance)}
            </Label>
          </Fieldset>
        </View>
        <XStack justifyContent="center" gap="$2" marginVertical="$6" $mobile={{ marginVertical: '$4' }}>
          <Button
            size="$6"
            width="$19"
            disabled={!isValid}
            disabledStyle={{ backgroundColor: '$pink10', borderColor: '$pink10', cursor: 'auto' }}
            onPress={handleRequestTransfer}>
            {getString('pointsPicker.cta')}
          </Button>
          <Link href={regionalizePath(backLink)} textDecorationColor="$transparent">
            <Button size="$6" width="$19" theme="secondary">
              {getString('actions.back')}
            </Button>
          </Link>
        </XStack>
      </Form>
      <Dialog modal open={modalOpen}>
        <Dialog.Portal>
          <Dialog.Overlay onPress={handleCancel} opacity={0.7} backgroundColor="$black10" />
          <Dialog.Content
            bordered
            size="$3"
            maxWidth={586}
            gap="$3"
            paddingVertical="$4"
            paddingHorizontal="$5"
            marginHorizontal="$5"
            $mobile={{ padding: '$3' }}>
            <Text fontSize="$8" fontWeight="600">
              {getString('pointsPicker.modal.title')}
            </Text>
            {renderDialogContent(value)}
            <Text fontSize="$4">{getString('pointsPicker.modal.note')}</Text>
            <XStack gap="$2" marginTop="$2" $mobile={{ flexDirection: 'column' }}>
              <Button paddingHorizontal="$2" height="$6" onPress={handleSubmit}>
                {getString('pointsPicker.modal.cta')}
              </Button>
              <Button height="$6" theme="secondary" paddingHorizontal="$7" onPress={handleCancel}>
                {getString('actions.cancel')}
              </Button>
            </XStack>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
    </>
  )
}

function getAvailableOptions(balance: number): number[] {
  const options = [1000, 5000, 15000, 30000]
  return options.filter((option) => option <= balance)
}
